import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from './index';
import TaskQueue from '../core/task-queue';
import { getContactFormDetails } from '../helpers/template.helper';
import { createPageForTemplate } from 'src/helpers/page.helper';
import createSyncTask from '../Tasks/syncTask';
import { TTemplate } from '../types/entity/template.type';
import { eventBus } from '../core/event-bus';
import { CustomError } from '../lib/error';
import logger from 'src/logger';
import { miniSerializeErrorOverride, SerializedError } from './serialize-error';
import { DisplayInModalErrors, ErrorCodes } from 'src/constants/error-codes';
import { getSessionIdentifier } from '../helpers/session.helpers';
import { getOrderFormContactEmails } from 'src/helpers/order-form.helper';

type SyncDraftActionArg = {
  template: TTemplate;
};

export const syncDraftAction = createAsyncThunk(
  'draft/save',
  async ({ template }: SyncDraftActionArg, { getState, dispatch }) => {
    const {
      session,
      fileStore: { fileIds },
      draftData: { localChangesVersion: LDV },
      siteData: { siteAttr },
    } = getState() as RootState;
    try {
      if (!session) throw new CustomError({ errorCode: 'SessionNotFound' });

      //prevent draft sync for DevOps publish pending sites
      if (siteAttr?.isDevOpsPublishPending) return;

      await new Promise((resolve, reject) => {
        const page = createPageForTemplate(template);
        const pageData = page.getData({ trim: true });
        const pageJSON = JSON.stringify(pageData);
        const contactFormData = getContactFormDetails(page);
        const orderFormContactEmails = getOrderFormContactEmails(template);

        const pageInfo = {
          page: pageJSON,
          fileIds: fileIds,
          ...contactFormData,
          siteProps: orderFormContactEmails,
        };

        // Create a task for saving the draft
        const Task = createSyncTask(
          {
            ...getSessionIdentifier(session),
            domainName: session.domainName,
            pageInfo,
            pagePath: pageData.route,
          },
          {
            onSuccess: () => {
              const { localChangesVersion: currentLDV } = (getState() as RootState).draftData;
              if (currentLDV === LDV) {
                dispatch(resetLocalChangeVersion());
              }
              return resolve(null as any);
            },
            onFailure: ({ error }) => reject(error),
          },
        );
        TaskQueue.enqueue(Task);
      });
      setTimeout(() => {
        dispatch(resetSaveProgress());
      }, 3000);
    } catch (error: any) {
      if (error instanceof CustomError && error.error === 'TASK_DEPENDENT_DEQUEUE') {
        return;
      }
      logger.reportError(error, { message: 'Error in syncDraftAction' });
      if (error.error === ErrorCodes.WebsitePublishInProgress) {
        eventBus.emit(
          'error/showToast',
          new CustomError({
            errorCode: ErrorCodes.WebsitePublishInProgress,
            desc: DisplayInModalErrors[ErrorCodes.WebsitePublishInProgress].desc,
          }),
        );
      } else {
        eventBus.emit('error/showToast', error);
      }
      throw error;
    }
  },
  {
    serializeError: miniSerializeErrorOverride,
  },
);

type TInitialState = {
  localChangesVersion: number;
  savingInProgress: boolean | undefined;
  error?: string | null;
};

const initialState: TInitialState = {
  localChangesVersion: 0,
  savingInProgress: undefined,
  error: null,
};

const DraftSyncSlice = createSlice({
  name: 'draftData',
  initialState,
  reducers: {
    resetLocalChangeVersion: state => {
      state.localChangesVersion = 0;
    },
    incrementLocalChangeVersion: state => {
      state.localChangesVersion += 1;
    },
    resetSaveProgress: state => {
      state.savingInProgress = undefined;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(syncDraftAction.pending, state => {
        state.savingInProgress = true;
        state.error = null;
      })
      .addCase(syncDraftAction.fulfilled, (state, action) => {
        state.savingInProgress = false;
        state.error = null;
      })
      .addCase(syncDraftAction.rejected, (state, action) => {
        state.savingInProgress = undefined;
        state.error = (action.error as SerializedError)?.error || action.error.message || 'Failed to save draft';
      });
  },
});

export const { resetLocalChangeVersion, incrementLocalChangeVersion, resetSaveProgress } = DraftSyncSlice.actions;

// Selector
export const LCVSelector = (state: RootState) => state.draftData.localChangesVersion;

export const DraftSaveSelector = (state: RootState) => state.draftData.savingInProgress;
export default DraftSyncSlice.reducer;
